import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Camera, ListChecks, MessageSquare, PackageOpen, Send } from "lucide-react";
import { Button, Checkbox, Typography } from "@akuna/ui";
import type { PanelPet } from "../panel/panelModel";
import { openWhatsAppWithText } from "../reservation/comandaQuoteText";
import { usePetReservationSummary } from "../reservation/usePetReservationSummary";
import { buildStayCheckoutText } from "./checkoutText";
import { SectionBoundary } from "./SectionBoundary";
import { formatStayCurrency } from "./stayFormat";
import { StayComandaSection } from "./StayComandaSection";
import { useStayCheckoutSummary } from "./useStayCheckoutSummary";
import { useStayComanda } from "./useStayComanda";
import { useReservationBillingTiming } from "./useReservationBillingTiming";
import { useStayMediaShareToken } from "./useStayMediaShare";

export interface StayCheckoutStepProps {
  pet: PanelPet;
  isoDate: string;
  deliveredItemIds: string[];
  onDeliveredItemsChange: (ids: string[]) => void;
}

const dayLabel = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

function SectionTitle({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="mb-2.5 flex items-center gap-2">
      {icon}
      <Typography variant="h5">{children}</Typography>
    </div>
  );
}

/**
 * A conferência de saída: o que o pet fez na estadia inteira, o que foi
 * observado, o que volta com o responsável e as fotos — e, embaixo, a comanda.
 */
export function StayCheckoutStep({ pet, isoDate, deliveredItemIds, onDeliveredItemsChange }: StayCheckoutStepProps) {
  const { t, i18n } = useTranslation("daycareHotel");
  const summary = useStayCheckoutSummary(pet.reservationId, pet.petId);
  const profile = usePetReservationSummary(pet.petId);
  const shareToken = useStayMediaShareToken(pet.reservationId);
  const comanda = useStayComanda(pet.reservationId);
  const billingTiming = useReservationBillingTiming(pet.reservationId).data;

  if (summary.isLoading || summary.isError || profile.isLoading || profile.isError) {
    return (
      <SectionBoundary
        isLoading={summary.isLoading || profile.isLoading}
        isError={summary.isError || profile.isError}
        skeletonClassName="h-64 rounded-2xl"
      />
    );
  }

  const days = summary.data?.days ?? [];
  const media = summary.data?.media ?? [];
  const storedItems = profile.data?.storedItems ?? [];

  const toggleItem = (id: string) => {
    onDeliveredItemsChange(
      deliveredItemIds.includes(id)
        ? deliveredItemIds.filter((current) => current !== id)
        : [...deliveredItemIds, id],
    );
  };

  const activityDays = days.filter((day) => day.activities.length > 0);
  const observationDays = days.filter((day) => day.observations.length > 0);

  const sendSummary = () => {
    const language = i18n.resolvedLanguage ?? i18n.language ?? "pt-BR";
    const comandaLines = comanda.data?.lines ?? [];
    const text = buildStayCheckoutText({
      petName: pet.petName,
      moduleLabel: t(`schedule.nature.${pet.nature}`),
      periodLabel: pet.stayRange ?? dayLabel(isoDate),
      days,
      deliveredItems: storedItems
        .filter((item) => deliveredItemIds.includes(item.id))
        .map((item) => item.itemName),
      comandaLines: comandaLines.length === 0 ? [] : [
        ...comandaLines.map((line) => `• ${line.quantity}x ${line.itemName} — ${formatStayCurrency(line.lineTotal, language)}`),
        `${t("panel.comandaTotal")}: ${formatStayCurrency(comanda.data?.total ?? 0, language)}`,
      ],
      galleryUrl: shareToken.data ? `${window.location.origin}/fotos/${shareToken.data}` : null,
      labels: {
        header: t("panel.checkoutSummaryHeader"),
        activities: t("panel.checkoutActivities"),
        observations: t("panel.checkoutObservations"),
        deliveredItems: t("panel.checkoutDeliveredItems"),
        comanda: t("panel.sectionComanda"),
        photos: t("panel.checkoutPhotosLabel"),
        done: t("panel.checkoutActivityDone"),
        pending: t("panel.checkoutActivityPending"),
      },
      formatDay: dayLabel,
    });
    openWhatsAppWithText({ phone: pet.ownerPhone, text });
  };

  return (
    <div className="grid gap-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <section>
          <SectionTitle icon={<ListChecks aria-hidden="true" size={18} className="text-akuna-orange" />}>
            {t("panel.checkoutActivities")}
          </SectionTitle>
          {activityDays.length === 0 ? (
            <Typography variant="body-sm" className="text-muted-foreground">{t("panel.checkoutActivitiesEmpty")}</Typography>
          ) : (
            <div className="flex flex-col gap-3">
              {activityDays.map((day) => (
                <div key={day.day}>
                  <Typography variant="caption" className="text-muted-foreground">{dayLabel(day.day)}</Typography>
                  <ul className="mt-1 flex flex-col gap-1.5">
                    {day.activities.map((activity) => (
                      <li key={activity.id} className="flex items-baseline gap-2 rounded-xl border border-border px-3 py-2">
                        {activity.time && (
                          <Typography variant="body-sm" className="shrink-0 tabular-nums text-muted-foreground">{activity.time}</Typography>
                        )}
                        <Typography variant="body-sm" className="font-semibold text-foreground">{activity.typeName}</Typography>
                        {activity.description && (
                          <Typography variant="body-sm" className="min-w-0 flex-1 truncate text-foreground">{activity.description}</Typography>
                        )}
                        <Typography variant="caption" className={activity.done ? "ml-auto shrink-0 text-state-success" : "ml-auto shrink-0 text-muted-foreground"}>
                          {activity.done ? t("panel.checkoutActivityDone") : t("panel.checkoutActivityPending")}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionTitle icon={<MessageSquare aria-hidden="true" size={18} className="text-akuna-blue" />}>
            {t("panel.checkoutObservations")}
          </SectionTitle>
          {observationDays.length === 0 ? (
            <Typography variant="body-sm" className="text-muted-foreground">{t("panel.checkoutObservationsEmpty")}</Typography>
          ) : (
            <div className="flex flex-col gap-3">
              {observationDays.map((day) => (
                <div key={day.day}>
                  <Typography variant="caption" className="text-muted-foreground">{dayLabel(day.day)}</Typography>
                  <ul className="mt-1 flex flex-col gap-1.5">
                    {day.observations.map((observation) => (
                      <li key={observation.id} className="flex items-baseline gap-2 rounded-xl border border-border px-3 py-2">
                        {observation.time && (
                          <Typography variant="body-sm" className="shrink-0 tabular-nums text-muted-foreground">{observation.time}</Typography>
                        )}
                        {observation.isIncident && (
                          <AlertTriangle aria-hidden="true" size={14} className="shrink-0 text-state-danger" />
                        )}
                        <Typography variant="body-sm" className="font-semibold text-foreground">{observation.typeName}</Typography>
                        <Typography variant="body-sm" className="min-w-0 flex-1 text-foreground">{observation.description}</Typography>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <section>
          <SectionTitle icon={<Camera aria-hidden="true" size={18} className="text-akuna-orange" />}>
            {t("panel.checkoutPhotos", { count: media.length })}
          </SectionTitle>
          {media.length === 0 ? (
            <Typography variant="body-sm" className="text-muted-foreground">{t("panel.mediaEmpty")}</Typography>
          ) : (
            <ul className="grid grid-cols-3 gap-2">
              {media.map((item) => (
                <li key={item.id} className="overflow-hidden rounded-xl border border-border">
                  {item.mediaKind === "image" ? (
                    <img src={item.signedUrl} alt={item.description} className="aspect-square w-full object-cover" />
                  ) : (
                    <video src={item.signedUrl} className="aspect-square w-full object-cover" aria-label={item.description} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <SectionTitle icon={<PackageOpen aria-hidden="true" size={18} className="text-neutral-500" />}>
            {t("panel.checkoutItems")}
          </SectionTitle>
          {storedItems.length === 0 ? (
            <Typography variant="body-sm" className="text-muted-foreground">{t("panel.storedItemsEmpty")}</Typography>
          ) : (
            <ul className="flex flex-col gap-2">
              {storedItems.map((item) => (
                <li key={item.id} className="flex items-center gap-2.5 rounded-xl border border-border px-3 py-2.5">
                  {/* `Checkbox` do @akuna/ui já envolve o input num <label>; o texto vai pela prop `label`. */}
                  <Checkbox
                    className="min-w-0 flex-1"
                    label={item.itemName}
                    checked={deliveredItemIds.includes(item.id)}
                    onCheckedChange={() => toggleItem(item.id)}
                  />
                  {item.storageLocation && (
                    <Typography variant="caption" className="shrink-0 text-muted-foreground">{item.storageLocation}</Typography>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <Button className="w-full" onClick={sendSummary}>
        <Send aria-hidden="true" size={16} className="mr-1.5" />
        {t("panel.checkoutSendWhatsApp")}
      </Button>

      {billingTiming === "check_out" ? (
        <p role="status" className="rounded-xl border border-state-warning/40 bg-state-warning-soft px-3 py-2 text-sm text-foreground">
          {t("hotelBilling.atCheckout")}
        </p>
      ) : null}
      <StayComandaSection reservationId={pet.reservationId} />
    </div>
  );
}
